/**
 * admin-svc /admin/agent-rate-card — the terms THIS agent's agreements are written
 * on (V061).
 *
 * V055 made the TXR-1501 terms brokerage policy; V061 makes them the agent's own
 * rate card. Every field is nullable: null = "inherit the brokerage default", and
 * the response carries the defaults alongside the overrides so an editor can show
 * which is which.
 *
 * An agent omits `agentId` and reaches their own card; a brokerage_admin /
 * super_admin must name the agent.
 */

import type { ApiClient } from "../client";

export interface BrokerageTermDefaults {
  market_area: string | null;
  county: string | null;
  purchase_fee_pct: string;
  lease_fee_pct: string;
  protection_period_days: number;
  representation_term_months: number;
}

export interface AgentRateCard {
  agent_id: string;
  /** REAL_ESTATE | MORTGAGE — these TXR-1501 terms are a Texas real estate
   * form and don't apply to a MORTGAGE agent's brokerage. */
  brokerage_type: string;
  /** The agent's overrides — null means the brokerage default applies. */
  market_area: string | null;
  county: string | null;
  purchase_fee_pct: string | null;
  lease_fee_pct: string | null;
  protection_period_days: number | null;
  representation_term_months: number | null;
  /** What each null above resolves to at send time; null when the tenant has no
   * brokerage profile yet. */
  brokerage_defaults: BrokerageTermDefaults | null;
}

/** A PATCH updates only what it names; naming a field as null clears the override
 * back to "inherit the brokerage default". */
export type AgentRateCardUpdate = Partial<
  Omit<AgentRateCard, "agent_id" | "brokerage_type" | "brokerage_defaults">
>;

/** An admin names the agent either by agents.id (`agentId`) or — what the Staff
 * roster actually holds — by users.id (`userId`); the server translates. An agent
 * passes neither. */
export interface RateCardTarget {
  agentId?: string;
  userId?: string;
}

function path(target: RateCardTarget = {}) {
  if (target.agentId)
    return `/admin/agent-rate-card?agent_id=${encodeURIComponent(target.agentId)}`;
  if (target.userId)
    return `/admin/agent-rate-card?user_id=${encodeURIComponent(target.userId)}`;
  return "/admin/agent-rate-card";
}

export const agentRateCardService = {
  get(client: ApiClient, target?: RateCardTarget) {
    return client.request<AgentRateCard>(path(target));
  },

  update(
    client: ApiClient,
    body: AgentRateCardUpdate,
    target?: RateCardTarget,
  ) {
    return client.request<AgentRateCard>(path(target), {
      method: "PATCH",
      body,
    });
  },
};
